import { VariantDefiningAttribute } from 'shared/types/product/Product';
import { ExtendedVariant } from 'shared/types/product/Variant';
import { textToColor } from 'helpers/textToColor/textToColor';
import { withTrailingSlash } from 'helpers/url/withTrailingSlash';

// Every variant of a product has its own `/slug/p/<sku>/` URL. Those pages are one product to a
// shopper and should be one page to a search engine, so they all point their canonical at the
// REPRESENTATIVE variant: the first in commercetools order (Joel curates that order offline; the
// product sitemap already lists that SKU and nothing else). The other variant URLs stay live and
// shareable — they just consolidate their signals onto one address.

export type VariantProduct = {
  name?: string;
  metaTitle?: string;
  variants?: ExtendedVariant[];
  variantDefiningAttributes?: VariantDefiningAttribute[];
};

export const representativeVariant = (product: VariantProduct): ExtendedVariant | undefined => product.variants?.[0];

export const variantPath = (slug: string, sku: string) => withTrailingSlash(`/${slug}/p/${sku}`);

export const canonicalVariantPath = (slug: string, product: VariantProduct, currentSku: string) =>
  variantPath(slug, representativeVariant(product)?.sku ?? currentSku);

const cleanValue = (value: unknown) => {
  const text = String(value ?? '').trim();
  // Colour values can arrive as "Label:#hex"; only the label is human-facing.
  return text.includes(':') ? textToColor(text).label : text;
};

// An axis with one value describes the product, not the variant, so it is never part of what a
// variant is distinguished by — in the title or in the structured data.
const selectableAxes = (product: VariantProduct) =>
  (product.variantDefiningAttributes ?? []).filter(
    (axis) => (axis.primitiveValues?.length ?? axis.values?.length ?? 0) > 1,
  );

// ---------------------------------------------------------------------------------------------
/**
 * `36 inches` / `3 Inch` / `36in` / `36 in` / `1/2-in` → `36"`: the inch mark is how the trade writes it and it
 * keeps titles short (Joel, 4 Oct 2026). A spaced "in" only counts when it ends the value or a list
 * item, or comes before a dimension word or "x" ("6 in long", "2 in x 4 in"), so the preposition in
 * "2 in 1 Tool" is left alone, and so is the hyphenated "2-in-1".
 */
export const inchMark = (text: string) =>
  text.replace(
    /(\d+\/\d+|\d+(?:\.\d+)?)(?:\s*(?:inches|inch)\b|-?in\b(?!-)|\s+in\b\.?(?=\s*(?:$|[,;)|×]|x\b|(?:long|wide|high|tall|thick|deep)\b)))/gi,
    '$1"',
  );

// The name a variant is shown and linked under: its commercetools variant name, falling back to the
// product name. (Composed "36" Black Express …" names were tried and reverted, 6 Oct 2026.)

/** The product noun: the commercetools product name as authored. */
export const productNoun = (product: VariantProduct) => product.name;

export const variantDisplayName = (product: VariantProduct, variant: ExtendedVariant): string =>
  variant.name || product.name || variant.sku;

/**
 * Every variant's name, kept distinct: variants without their own name all fall back to the product
 * name, so a repeated name gets the SKU appended — otherwise every tile and ProductGroup entry for
 * those variants would read the same.
 */
export const variantDisplayNames = (product: VariantProduct): Map<string, string> => {
  const variants = product.variants ?? [];
  const counts = new Map<string, number>();
  variants.forEach((variant) => {
    const name = variantDisplayName(product, variant);
    counts.set(name, (counts.get(name) ?? 0) + 1);
  });
  return new Map(
    variants.map((variant) => {
      const name = variantDisplayName(product, variant);
      return [variant.sku, (counts.get(name) ?? 0) > 1 ? `${name} (${variant.sku})` : name];
    }),
  );
};

/**
 * "More Surface Mount Corner Cable Railing Posts, with Handrail": the noun before any comma is
 * pluralised; names with digits or other punctuation ("3\" Thick Stair Tread - Signature") don't
 * pluralise cleanly, so they read "More options for …".
 */
export const variantListHeading = (name?: string): string => {
  if (!name) return 'Also available';
  // Pluralise the head noun phrase, before any qualifier: ", with Handrail", " with Base Rail", " for Wood Posts".
  const m = name.match(/^(.*?)(,.*| with .*| for .*)?$/);
  const noun = (m?.[1] ?? name).trim();
  const qualifier = m?.[2] ?? '';
  if (!/^[A-Za-z ]+$/.test(noun)) return `More options for ${name}`;
  const plural = /s$/i.test(noun) ? noun : `${noun}s`;
  return `More ${plural}${qualifier}`;
};

type VariesByProperty = 'color' | 'material' | 'size';

// Axes that are what the product is made of. Everything else that isn't a colour is published as
// `size`, the closest of the properties Google accepts in `variesBy`.
const MATERIAL_AXIS_KEYS = ['material', 'wood-type', 'species'];

const variesByProperty = (axis: VariantDefiningAttribute): VariesByProperty => {
  if (axis.isColorAttribute) return 'color';
  return MATERIAL_AXIS_KEYS.includes(axis.key) ? 'material' : 'size';
};

/** The schema.org properties a ProductGroup varies by: one per property its selectable axes map to. */
export const variesByFor = (product: VariantProduct) => [
  ...new Set(selectableAxes(product).map((axis) => `https://schema.org/${variesByProperty(axis)}`)),
];

/** A variant's value for each property in `variesByFor` — "Aluminum" as material, "42", Surface" as size. */
export const variantPropertyValues = (product: VariantProduct, variant: ExtendedVariant) => {
  const values: Partial<Record<VariesByProperty, string[]>> = {};
  for (const axis of selectableAxes(product)) {
    const value = inchMark(cleanValue(variant.attributes?.[axis.key]));
    if (!value) continue;
    const property = variesByProperty(axis);
    values[property] = [...(values[property] ?? []), value];
  }
  return Object.fromEntries(Object.entries(values).map(([property, list]) => [property, list.join(', ')])) as Partial<
    Record<VariesByProperty, string>
  >;
};
